import { useState, useEffect, useContext } from "react";
import { ServiceContext } from "@msx/platform-services";
import { getConsumptionAPI } from "../../../../../app/utils/httpUtils";

/**
 * useSessionTokens — Manages SAS token and environment variables lifecycle.
 *
 * Extracted from V2_Shell.tsx (PR #1, Phase 1).
 * Zero logic changes — pure cut-paste extraction.
 *
 * useState moved: isSasLoaded, isSasCalled, envVariableCalled, envVariableLoaded,
 *                 getAccessTokenForAPICall, getIrisToken
 * Functions moved: getStorageSasToken, getEnvVariables
 * Effects moved: Effect #8 (SAS/env auto-refresh on expiry + online/offline)
 */
export const useSessionTokens = (handleErrorResponse: (error: any) => void) => {
  const context = useContext(ServiceContext);

  const [isSasLoaded, setSasLoaded] = useState(false);
  const [isSasCalled, setSasCalled] = useState(false);
  const [envVariableCalled, setEnvVarCalled] = useState(false);
  const [envVariableLoaded, setEnvVarLoaded] = useState(false);
  const [getAccessTokenForAPICall, setAccessTokenForAPIcall] = useState(null);
  const [getIrisToken] = useState(null);

  const getStorageSasToken = async () => {
    try {
      const sastokenresponse = await getConsumptionAPI(
        `/api/User/GetSasTokenKey`,
        context.authClient
      );
      if (sastokenresponse.status === 200) {
        sessionStorage.setItem(
          "SasTokenKey",
          "?" + sastokenresponse.data.split("{_URL:}")[0]
        );
        setSasLoaded(true);
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  const getEnvVariables = async () => {
    try {
      const response = await getConsumptionAPI(
        `/api/User/GetEnvironmentVariables`,
        context.authClient
      );
      if (response.status === 200) {
        sessionStorage.setItem(
          "EnvironmentVariables",
          JSON.stringify(response?.data)
        );
        setEnvVarLoaded(true);
      }
    } catch (error) {
      handleErrorResponse(error);
    }
  };

  //8.effect
  useEffect(() => {
    if (sessionStorage.getItem("SasTokenKey")) {
      const clearSessionStorageByKey = () => {
        sessionStorage.removeItem("SasTokenKey");
        setSasLoaded(false);
        getStorageSasToken();
      };

      // Set up the timeout to clear the session storage in 59 minutes (3540 seconds)
      setTimeout(clearSessionStorageByKey, 60 * 60 * 1000);
      const handleOffline = () => {
        clearSessionStorageByKey();
      };

      // Add event listeners for offline and online events
      window.addEventListener("offline", handleOffline);

      // Cleanup function
      return () => {
        window.removeEventListener("offline", handleOffline);
      };
    }

    if (sessionStorage.getItem("EnvironmentVariables")) {
      const clearSessionStorageByKey = () => {
        sessionStorage.removeItem("EnvironmentVariables");
        setEnvVarLoaded(false);
        getEnvVariables();
      };

      // Set up the timeout to clear the session storage in 59 minutes (3540 seconds)
      setTimeout(clearSessionStorageByKey, 60 * 60 * 1000);
      const handleOffline = () => {
        clearSessionStorageByKey();
      };

      // Add event listeners for offline and online events
      window.addEventListener("offline", handleOffline);

      // Cleanup function
      return () => {
        window.removeEventListener("offline", handleOffline);
      };
    }

    const handleOnline = () => {
      if (!sessionStorage.getItem("SasTokenKey")) {
        getStorageSasToken();
      }
      if (!sessionStorage.getItem("EnvironmentVariables")) {
        getEnvVariables();
      }
    };
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [isSasLoaded, envVariableLoaded, getAccessTokenForAPICall?.token]);

  return {
    isSasLoaded,
    setSasLoaded,
    isSasCalled,
    setSasCalled,
    envVariableCalled,
    setEnvVarCalled,
    envVariableLoaded,
    setEnvVarLoaded,
    getAccessTokenForAPICall,
    setAccessTokenForAPIcall,
    getIrisToken,
    getStorageSasToken,
    getEnvVariables,
  };
};
